import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, Lock, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SidebarGroupProps {
  icon: LucideIcon
  label: string
  isExpanded: boolean
  onToggle: () => void
  isActive: boolean
  sidebarOpen: boolean
  subscribed?: boolean
  children: React.ReactNode
}

/**
 * SidebarGroup — expandable navigation group with Framer Motion animation.
 *
 * Used for modules that have sub-routes (e.g., QR Attendance, Stock).
 * In collapsed sidebar mode (64px), renders only the icon.
 * When `subscribed === false`, applies muted styling and a Lock badge.
 */
export function SidebarGroup({
  icon: Icon,
  label,
  isExpanded,
  onToggle,
  isActive,
  sidebarOpen,
  subscribed = true,
  children,
}: SidebarGroupProps) {
  const chevronVariants = {
    closed: { rotate: 0 },
    open: { rotate: 180 },
  }

  const isNotSubscribed = !subscribed

  return (
    <div>
      {/* Group header button */}
      <button
        onClick={onToggle}
        className={cn(
          'group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150',
          isActive
            ? 'bg-[#00a76f29] text-white border-l-2 border-[#00ab81]'
            : isNotSubscribed
              ? 'text-[#637381] hover:bg-[#252f3a]'
              : 'text-[#919eab] hover:bg-[#252f3a] hover:text-white',
          !sidebarOpen && 'justify-center px-0',
          isNotSubscribed && 'opacity-60',
        )}
        aria-expanded={isExpanded}
        aria-label={label}
      >
        <Icon className={cn('h-5 w-5 shrink-0', isNotSubscribed && !sidebarOpen && 'opacity-60')} />

        {sidebarOpen && (
          <>
            <span className="flex-1 text-left whitespace-nowrap">{label}</span>
            {isNotSubscribed ? (
              <Lock className="h-3 w-3 shrink-0 text-surface-500" />
            ) : (
              <motion.div
                animate={isExpanded ? 'open' : 'closed'}
                variants={chevronVariants}
                transition={{ duration: 0.15, ease: 'easeInOut' }}
              >
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-surface-500" />
              </motion.div>
            )}
          </>
        )}
      </button>

      {/* Sub-items: animated expand/collapse when sidebar is open, instant hide when closing */}
      {sidebarOpen ? (
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="ml-3 space-y-0.5 border-l border-[#ffffff14] pl-3 py-0.5">
                {children}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      ) : isExpanded ? (
        <div className="overflow-hidden h-0" />
      ) : null}
    </div>
  )
}
